import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"
import { Icons } from "../icons"
import { MapIcon } from "./map-icon"

interface MapLegendsProps {
  onStatusFilterChange: (statuses: string[]) => void
  selectedStatuses: string[]
}

export function MapLegends({ onStatusFilterChange, selectedStatuses }: MapLegendsProps) {
  const [isFilterMode, setIsFilterMode] = useState(false)
  
  const statuses = [
    "Active",
    "Disconnected",
    "Delinquent",
    "Pending",
    "Before Due",
    "Due Today",
    "Over Due",
  ]

  const toggleStatusSelection = (status: string) => {
    const newStatuses = selectedStatuses.includes(status)
      ? selectedStatuses.filter(s => s !== status)
      : [...selectedStatuses, status]
    onStatusFilterChange(newStatuses)
  }

  const toggleAllStatuses = () => {
    if (selectedStatuses.length === statuses.length) {
      onStatusFilterChange([])
    } else {
      onStatusFilterChange([...statuses])
    }
  }

  return (
    <Card className="w-full max-w-xs rounded-lg shadow-lg overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between bg-primary p-3 space-y-0">
        <CardTitle className="text-white text-lg font-semibold">Legend</CardTitle>
        <div className="flex items-center space-x-2">
          {isFilterMode && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-white hover:bg-primary/80 text-xs"
              onClick={toggleAllStatuses}
            >
              {selectedStatuses.length === statuses.length ? "Deselect all" : "Select all"}
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:bg-primary/80"
            onClick={() => setIsFilterMode(!isFilterMode)}
          >
            <Icons.listFilter className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          {statuses.map((status) => (
            <div 
              key={status} 
              className={`flex items-center gap-2 p-2 rounded-md transition-colors ${
                isFilterMode && selectedStatuses.includes(status) 
                  ? "bg-primary/10" 
                  : "hover:bg-muted"
              }`}
              onClick={() => isFilterMode && toggleStatusSelection(status)}
            >
              {isFilterMode ? (
                <Checkbox 
                  id={status} 
                  checked={selectedStatuses.includes(status)}
                  className="w-4 h-4"
                  onCheckedChange={() => toggleStatusSelection(status)}
                />
              ) : (
                <MapIcon connectionStatus={status} />
              )}
              <label 
                htmlFor={status} 
                className="text-sm font-medium leading-none cursor-pointer"
              >
                {status}
              </label>
            </div>
          ))}
        </div>
        
        {isFilterMode && (
          <div className="pt-2 border-t">
            <Button 
              variant="default" 
              size="sm" 
              className="w-full"
              onClick={() => setIsFilterMode(false)}
            >
              Apply Filters ({selectedStatuses.length})
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}